import { useQuery } from '@tanstack/react-query';
import { useUniversalNavContext } from '../../universal-nav/hooks/useUniversalNavContext';
import type { Plan, PlanGroup, PlanType } from '../../types';

interface PlanRaw {
  plan_type: PlanType;
  actions: number;
  off_plan_group?: boolean;

  id: number;
  name: string;
  interval: 'month' | 'year';
  amount: number;
}

// Refer to https://gitlab.com/zapier/zapier/-/blob/main/graphql-proxy/src/structures/Plan/utils/groupPlansByPlanType/index.js#L7 for original implementation.
const groupPlansByPlanType = (planData: Array<PlanRaw>): Array<PlanGroup> => {
  // useAlertBannerData's getNextPlan logic won't work correctly unless plans are grouped in this order
  const planTypes: Array<PlanType> = [
    'Free',
    'Starter',
    'Professional',
    'Team',
    'Company',
  ];

  const planGroups: Array<PlanGroup> = [];

  for (const planType of planTypes) {
    const plansInGroup = planData
      .filter((plan) => plan.plan_type === planType)
      .sort((a, b) => a.actions - b.actions)
      .map<Plan>((plan) => ({
        id: plan.id,
        name: plan.name,
        interval: plan.interval,
        amount: plan.amount,
        type: plan.plan_type,
        isInPlanGroup: !plan.off_plan_group || false,
        actions: plan.actions,
      }));

    planGroups.push({
      planType,
      plans: plansInGroup,
    });
  }

  return planGroups;
};

export const useQueryPlans = (currentAccountId?: number) => {
  const { apiHostname } = useUniversalNavContext();

  const result = useQuery({
    queryKey: ['accountPlans', currentAccountId],
    queryFn: async () => {
      const endpoint = `${apiHostname}/api/v3/accounts/${currentAccountId}/plans`;

      const response = await fetch(endpoint, {
        credentials:
          process.env.NODE_ENV !== 'test' ? 'include' : 'same-origin',
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const plansJson = await response.json();
      return plansJson.objects;
    },
    enabled: !!currentAccountId,
    select: groupPlansByPlanType,
  });

  return {
    ...result,
    isLoading: result.isInitialLoading,
  };
};
